import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild
} from '@angular/core';

import {
  ControlContainer,
  NgForm,
  NgModel
} from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { BigDecimalService }    from '../../../core/services/big-decimal.service';
import { SessionService }       from '../../../sessions/session.service';
import { TextControlComponent } from '../text-control/text-control.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector       : 'wor-number-control',
  template: require('./number-control.component.html'),

  viewProviders: [
    {
      provide    : ControlContainer,
      useExisting: NgForm
    }
  ]
})
export class NumberControlComponent extends TextControlComponent {
  public currencySymbol = this.sessionService.currency().symbol;

  // Sent when the field is emptied, e.g. 0 where the AngularJS field sent 0.
  @Input() emptyValue    : number | string = '';
  @Input() isCurrency    = false;
  @Input() isPercentage  = false;
  @Input() isReadonly    = false;
  @Input() max          ?: number;
  // null gives no lower bound.
  @Input() min           = 0;
  // Replaces the too small and too big texts.
  @Input() rangeMessage ?: string;
  @Input() round         = 2;

  @Input() set model ( _model : any ) {
    // The empty value shows as a blank field, as it did in AngularJS.
    if ( this.emptyValue !== '' && _model === this.emptyValue ) {
      this._model = '';
    }
    else if ( this.control && this.control.valid ) {
      this._model = this.roundModel(_model);
    }
    else {
      this._model = _model;
    }
  };

  @ViewChild('input') control : NgModel;

  constructor (
    private bigDecimalService: BigDecimalService,
    private sessionService   : SessionService,
    private translateService : TranslateService
  ) {
    super();
  }

  getLabel ( control : NgModel ) : string {
    if ( this.rangeMessage && ( control.errors?.min || control.errors?.max ) ) {
      return this.rangeMessage;
    }

    if ( control.errors?.min ) {
      return this.translateService.instant('VALIDATIONS.NUMBER.TOO_SMALL', {
        min: this.min
      });
    }

    if ( control.errors?.max ) {
      return this.translateService.instant('VALIDATIONS.NUMBER.TOO_BIG', {
        max: this.max
      });
    }

    if ( control.errors?.number ) {
      return this.translateService.instant('VALIDATIONS.NUMBER.INVALID');
    }

    return this.label;
  }

  handleModelChange ( _model : string ) : void {
    if ( this.control && this.control.value && this.control.valid ) {
      this.model = this.roundModel(_model);

      this.modelChange.emit(this.model);
    }

  }

  onModelChange () : void {
    const typed = typeof this._model === 'string' ? this._model.trim() : this._model;

    // Spaces alone are sent as typed, so the field stays invalid instead of being emptied.
    if ( typed === '' && this._model !== '' ) {
      this.modelChange.emit(this._model);

      return;
    }

    const isEmpty = typed === '' || typed === null || typed === undefined;

    this.modelChange.emit(isEmpty && this.emptyValue !== '' ? this.emptyValue : typed);
  }

  roundModel ( model : string ) : string {
    const value         = typeof model === 'string' ? model.trim() : model;
    const unscaledValue = this.bigDecimalService.get(value);

    return ( unscaledValue.scale() > this.round )
      ? this.bigDecimalService.get(value, this.round).toString()
      : value;
  }
}
